/**
 * Routing Configuration for Medical Workflow Navigation
 * Mirrors existing patient-centric route structure preserving medical workflow sequences
 */

import { DeepLinkingConfig, MedicalRouteConfig, ProgressiveLoadingConfig, AuthGuardConfig } from '@/types/routing';

// Deep linking configuration preserving existing backend URL patterns
export const deepLinkConfig: DeepLinkingConfig = {
  // Patient routes mirroring backend: /patient/{patient_directory}
  patientDetail: (patientId: string) => `/patient/${patientId}`,
  patientStudies: (patientId: string) => `/patient/${patientId}/studies`,
  patientReports: (patientId: string) => `/patient/${patientId}/reports`,
  
  // Study routes preserving existing structure
  studyViewer: (patientId: string, studyId: string) => `/patient/${patientId}/study/${studyId}`,
  studyMeasurements: (patientId: string, studyId: string) => `/patient/${patientId}/study/${studyId}/measurements`,
  studySeries: (patientId: string, studyId: string, seriesId?: string) => 
    seriesId ? `/patient/${patientId}/study/${studyId}/series/${seriesId}` : `/patient/${patientId}/study/${studyId}/series`,
  
  // Report routes mirroring backend patterns: /report/* and /patient/{id}/report/*
  reportBuilder: (patientId: string, reportTypeId?: string) => 
    reportTypeId ? `/patient/${patientId}/report/builder/${reportTypeId}` : `/patient/${patientId}/report/builder`,
  reportEditor: (patientId: string, reportId: string) => `/patient/${patientId}/report/edit/${reportId}`,
  reportPreview: (patientId: string, reportId: string) => `/patient/${patientId}/report/preview/${reportId}`,
  reportForm: (patientId: string, reportTypeId: string) => `/patient/${patientId}/report/form/${reportTypeId}`,
  dynamicReport: (patientId: string, reportTypeId: string) => `/patient/${patientId}/report/dynamic/${reportTypeId}`,
  
  // DICOM routes preserving existing processing workflow
  dicomViewer: (patientId: string, studyId: string, instanceId?: string) => 
    instanceId ? `/patient/${patientId}/dicom/${studyId}/${instanceId}` : `/patient/${patientId}/dicom/${studyId}`,
  dicomUpload: (patientId: string) => `/patient/${patientId}/dicom/upload`,
  dicomProcessing: (patientId: string, studyId: string) => `/patient/${patientId}/dicom/${studyId}/process`,
  
  // Admin routes
  adminDashboard: () => '/admin',
  userManagement: () => '/admin/users',
  institutionManagement: () => '/admin/institutions',
  systemSettings: () => '/admin/settings'
};

// Medical route configurations preserving existing workflow sequences
export const medicalRoutes: MedicalRouteConfig[] = [
  // Dashboard and overview
  {
    path: '/',
    workflowStep: 'patient-selection',
    requiresAuth: true,
    requiresPatientContext: false,
    requiresStudyContext: false,
    preloadData: ['recent-patients', 'user-preferences', 'institution-config'],
    breadcrumbLabel: 'Dashboard',
    navigationTitle: 'Panel Principal'
  },
  
  // Patient management routes
  {
    path: '/patients',
    workflowStep: 'patient-selection',
    requiresAuth: true,
    requiresPatientContext: false,
    requiresStudyContext: false,
    preloadData: ['patient-list', 'patient-filters'],
    breadcrumbLabel: 'Pacientes',
    navigationTitle: 'Gestión de Pacientes'
  },
  
  // Patient detail routes
  {
    path: '/patient/:patientId',
    workflowStep: 'study-review',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['patient-details', 'patient-studies', 'patient-reports', 'dicom-thumbnails'],
    breadcrumbLabel: 'Detalle del Paciente',
    navigationTitle: 'Información del Paciente'
  },
  
  {
    path: '/patient/:patientId/studies',
    workflowStep: 'study-review',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['patient-studies', 'dicom-metadata', 'study-timeline'],
    breadcrumbLabel: 'Estudios',
    navigationTitle: 'Estudios del Paciente'
  },
  
  // Study viewer routes
  {
    path: '/patient/:patientId/study/:studyId',
    workflowStep: 'measurement',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: true,
    preloadData: ['study-details', 'dicom-images', 'measurement-tools', 'loinc-terms'],
    breadcrumbLabel: 'Visor de Estudio',
    navigationTitle: 'Visualización de Estudio DICOM'
  },
  
  {
    path: '/patient/:patientId/study/:studyId/measurements',
    workflowStep: 'measurement',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: true,
    preloadData: ['study-measurements', 'calculation-formulas', 'reference-ranges'],
    breadcrumbLabel: 'Mediciones',
    navigationTitle: 'Mediciones y Cálculos'
  },
  
  // Report routes preserving existing workflow
  {
    path: '/patient/:patientId/reports',
    workflowStep: 'report-review',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['patient-reports', 'report-templates', 'report-history'],
    breadcrumbLabel: 'Reportes',
    navigationTitle: 'Reportes del Paciente'
  },
  
  {
    path: '/patient/:patientId/report/builder',
    workflowStep: 'report-creation',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['report-templates', 'form-config', 'loinc-terms', 'patient-data'],
    breadcrumbLabel: 'Constructor de Reportes',
    navigationTitle: 'Crear Nuevo Reporte'
  },
  
  {
    path: '/patient/:patientId/report/builder/:reportTypeId',
    workflowStep: 'report-creation',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['report-template', 'form-structure', 'validation-rules', 'data-sync-config'],
    breadcrumbLabel: 'Creando Reporte',
    navigationTitle: 'Constructor de Reporte Especializado'
  },
  
  {
    path: '/patient/:patientId/report/form/:reportTypeId',
    workflowStep: 'report-creation',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['universal-form', 'report-type-config', 'medical-calculations'],
    breadcrumbLabel: 'Formulario de Reporte',
    navigationTitle: 'Formulario Universal de Reporte'
  },
  
  {
    path: '/patient/:patientId/report/dynamic/:reportTypeId',
    workflowStep: 'report-creation',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['dynamic-template', 'form-structure', 'sync-config', 'validation-schema'],
    breadcrumbLabel: 'Reporte Dinámico',
    navigationTitle: 'Generador de Reporte Dinámico'
  },
  
  {
    path: '/patient/:patientId/report/edit/:reportId',
    workflowStep: 'report-creation',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['report-data', 'form-config', 'revision-history', 'collaboration-locks'],
    breadcrumbLabel: 'Editando Reporte',
    navigationTitle: 'Editor de Reporte'
  },
  
  {
    path: '/patient/:patientId/report/preview/:reportId',
    workflowStep: 'report-review',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['report-content', 'print-template', 'signature-data', 'validation-results'],
    breadcrumbLabel: 'Vista Previa',
    navigationTitle: 'Vista Previa del Reporte'
  },
  
  // DICOM processing routes
  {
    path: '/patient/:patientId/dicom/upload',
    workflowStep: 'study-review',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: false,
    preloadData: ['upload-config', 'processing-queue', 'storage-quotas'],
    breadcrumbLabel: 'Subir DICOM',
    navigationTitle: 'Subida de Archivos DICOM'
  },
  
  {
    path: '/patient/:patientId/dicom/:studyId',
    workflowStep: 'measurement',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: true,
    preloadData: ['dicom-viewer', 'image-cache', 'measurement-tools', 'annotation-tools'],
    breadcrumbLabel: 'Visor DICOM',
    navigationTitle: 'Visualizador de Imágenes DICOM'
  },
  
  {
    path: '/patient/:patientId/dicom/:studyId/process',
    workflowStep: 'measurement',
    requiresAuth: true,
    requiresPatientContext: true,
    requiresStudyContext: true,
    preloadData: ['processing-status', 'extraction-results', 'equipment-data', 'metadata'],
    breadcrumbLabel: 'Procesando DICOM',
    navigationTitle: 'Procesamiento de Estudio DICOM'
  },
  
  // Admin routes
  {
    path: '/admin',
    workflowStep: 'patient-selection',
    requiresAuth: true,
    requiresPatientContext: false,
    requiresStudyContext: false,
    preloadData: ['admin-dashboard', 'system-metrics', 'user-activity'],
    breadcrumbLabel: 'Administración',
    navigationTitle: 'Panel de Administración'
  },
  
  {
    path: '/admin/users',
    workflowStep: 'patient-selection',
    requiresAuth: true,
    requiresPatientContext: false,
    requiresStudyContext: false,
    preloadData: ['user-list', 'role-definitions', 'institution-assignments'],
    breadcrumbLabel: 'Gestión de Usuarios',
    navigationTitle: 'Administración de Usuarios'
  },
  
  {
    path: '/admin/institutions',
    workflowStep: 'patient-selection',
    requiresAuth: true,
    requiresPatientContext: false,
    requiresStudyContext: false,
    preloadData: ['institution-list', 'configuration-templates', 'license-status'],
    breadcrumbLabel: 'Instituciones',
    navigationTitle: 'Gestión de Instituciones'
  }
];

// Progressive loading configuration preserving existing medical data access patterns
export const progressiveLoadingConfig: ProgressiveLoadingConfig = {
  immediate: {
    userAuth: true,
    patientBasicInfo: true,
    institutionContext: true
  },
  priority: {
    patientFullDetails: true,
    recentStudies: true,
    activeReports: true,
    loincTerminology: true
  },
  background: {
    patientHistory: true,
    dicomThumbnails: true,
    reportTemplates: true,
    equipmentData: true,
    systemMetrics: true
  },
  onDemand: {
    fullDicomImages: true,
    detailedMeasurements: true,
    historicalReports: true,
    auditLogs: true
  }
};

// Authentication guard configurations compatible with existing hybrid cookie/JWT system
export const authGuardConfigs: Record<string, AuthGuardConfig> = {
  public: {
    requiresAuthentication: false,
    requiresVerification: false,
    allowedRoles: [],
    redirectOnFailure: '/login',
    preserveIntendedRoute: true,
    checkInterval: 30000
  },
  
  authenticated: {
    requiresAuthentication: true,
    requiresVerification: true,
    allowedRoles: ['doctor', 'admin', 'superuser'],
    redirectOnFailure: '/login',
    preserveIntendedRoute: true,
    checkInterval: 60000
  },
  
  admin: {
    requiresAuthentication: true,
    requiresVerification: true,
    allowedRoles: ['admin', 'superuser'],
    redirectOnFailure: '/unauthorized',
    preserveIntendedRoute: false,
    checkInterval: 30000
  },
  
  superuser: {
    requiresAuthentication: true,
    requiresVerification: true,
    allowedRoles: ['superuser'],
    redirectOnFailure: '/unauthorized',
    preserveIntendedRoute: false,
    checkInterval: 30000
  }
};

// Route groups for efficient navigation management
export const routeGroups = {
  public: [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/verify-email',
    '/auth/reset-password',
  ],
  patient: [
    '/patient/:patientId',
    '/patient/:patientId/studies',
    '/patient/:patientId/reports',
    '/patient/:patientId/study/:studyId',
    '/patient/:patientId/dicom/:studyId'
  ],
  reports: [
    '/patient/:patientId/report/builder',
    '/patient/:patientId/report/form/:reportTypeId',
    '/patient/:patientId/report/dynamic/:reportTypeId',
    '/patient/:patientId/report/edit/:reportId',
    '/patient/:patientId/report/preview/:reportId'
  ],
  dicom: [
    '/patient/:patientId/dicom/upload',
    '/patient/:patientId/dicom/:studyId',
    '/patient/:patientId/dicom/:studyId/process'
  ],
  admin: [
    '/admin',
    '/admin/users',
    '/admin/institutions',
    '/admin/settings',
    '/admin/audit'
  ]
};

// Medical workflow sequences preserving existing clinical progression
export const workflowSequences = {
  standardExam: [
    'patient-selection',
    'study-review',
    'measurement',
    'report-creation',
    'report-review',
    'finalization'
  ],
  quickReport: [
    'patient-selection',
    'report-creation',
    'finalization'
  ],
  dicomProcessing: [
    'patient-selection',
    'study-review',
    'measurement',
    'report-creation'
  ],
  administrativeReview: [
    'patient-selection',
    'report-review',
    'finalization'
  ]
};
